/** @jsxImportSource @emotion/react */

import { CSSObject } from '@emotion/react';

import Clickable from '../components/clickable';
import Loader from '../components/loader';
import QrCode from '../components/qrCode';
import RemoteImage from '../components/RemoteImage';
import useTheme from '../hooks/useTheme';
import useLanguage from '../hooks/useLanguage';
import { IBaseOverlay } from '../contexts/overlayContext';
import { ISwedishEidLoginAction } from '../contexts/appConfigContext';

export interface ISwedishEidOverlay extends IBaseOverlay {
  texts: ISwedishEidLoginAction['texts'];
  logo?: ISwedishEidLoginAction['logo'];
  showQrCode: boolean;
  qrCode?: string;
  // Not set when the eID app can't be opened on the other kind of device.
  onSwitchDevice?: () => void;
  onCancel: () => void;
}

// The instructions come as one text with a numbered step per line, shown here as a list.
const toSteps = (instructions: string) =>
  instructions
    .split('\n')
    .map((line) => line.replace(/^\s*\d+\.\s*/, '').trim())
    .filter(Boolean);

const SwedishEidOverlay = ({
  texts,
  logo,
  showQrCode,
  qrCode,
  onSwitchDevice,
  onCancel,
}: ISwedishEidOverlay) => {
  const { color, font } = useTheme();
  const language = useLanguage();

  const BASE_STYLE: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    alignItems: 'center',
    textAlign: 'center',
    padding: 20,
    // The overlay is a screen high and centres this, so a phone shows all of it only by scrolling here.
    maxHeight: '100%',
    overflowY: 'auto',

    // Scroll rather than squeeze the buttons when it doesn't fit.
    '> *': {
      flexShrink: 0,
    },
    color: color.LOCAL_WHITE,
  };

  const TITLE_STYLE: CSSObject = {
    fontFamily: font.FAMILY_BOLD,
    fontSize: 20,
    marginBottom: 8,
  };

  return (
    <div css={BASE_STYLE}>
      {showQrCode ? (
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {qrCode && (
            <div css={{ padding: 10, backgroundColor: color.LOCAL_WHITE }}>
              <QrCode qrCode={qrCode} />
            </div>
          )}
          <div
            css={{
              alignSelf: 'stretch',
              textAlign: 'left',
              padding: '0 20px',
              fontSize: 17,
              marginTop: 20,
            }}
          >
            <div css={{ fontFamily: font.FAMILY_BOLD, marginBottom: 12 }}>
              {texts.qrCodeTitle}
            </div>
            <ol
              css={{
                marginLeft: 18,

                li: {
                  marginBottom: 8,
                  paddingLeft: 8,
                },
              }}
            >
              {toSteps(texts.qrCodeMessage).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      ) : (
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {logo && (
            <RemoteImage
              {...logo}
              css={{ width: 80, height: 80, marginBottom: 20 }}
            />
          )}
          <div
            css={{
              position: 'relative',
              width: 28,
              height: 28,
              marginBottom: 20,
            }}
          >
            <Loader
              transparent
              strokeColor={color.LOCAL_WHITE}
              show
              size={28}
              stroke={2}
            />
          </div>
          <div css={TITLE_STYLE}>{texts.waitingTitle}</div>
          <div css={{ fontSize: 17 }}>{texts.waitingMessage}</div>
        </div>
      )}

      {onSwitchDevice && showQrCode && (
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'stretch',
            marginTop: 4,
            fontSize: 15,
            textTransform: 'uppercase',
            opacity: 0.8,

            '::before, ::after': {
              content: "''",
              flex: 1,
              height: 1,
              backgroundColor: color.LOCAL_WHITE,
              opacity: 0.5,
            },
          }}
        >
          <span css={{ padding: '0 12px' }}>{language.get('or')}</span>
        </div>
      )}

      {onSwitchDevice && (
        <Clickable
          styleAs='button'
          css={{
            marginTop: 24,
            color: color.LOCAL_WHITE,
            backgroundColor: 'transparent',
            border: `1px solid ${color.LOCAL_WHITE}`,

            '&[active]': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          onClick={onSwitchDevice}
        >
          {showQrCode ? texts.openOnThisDevice : texts.openOnOtherDevice}
        </Clickable>
      )}

      <Clickable
        css={{
          marginTop: 16,
          padding: 10,
          fontSize: 17,
          color: color.LOCAL_WHITE,
        }}
        onClick={onCancel}
      >
        {texts.cancel}
      </Clickable>
    </div>
  );
};

export default SwedishEidOverlay;
